/**
 * Container for a single tappable formation chip in the formation chip-grid
 * selector. Binds the formation option's value to the onSelect callback and
 * renders the shared SelectableChip molecule (SE-5527) — the former local
 * View (and its isDark class dictionary) is gone; theme-awareness comes from
 * the molecule's semantic tokens.
 * @module features/squad-planning/components/FormationChip/FormationChipContainer
 */

import React, { memo, useCallback } from "react";

import type { FormationOption } from "@/features/squad-planning/types";

import SelectableChip from "@/components/molecules/SelectableChip";

/** Props for {@link FormationChipContainer}. */
export interface FormationChipContainerProps {
  readonly option: FormationOption;
  readonly isSelected: boolean;
  readonly onSelect: (value: string) => void;
}

/**
 * Binds a formation option's value to the onSelect callback.
 * @param props - Chip props
 * @param props.option - The formation option to render
 * @param props.isSelected - Whether this formation is currently active
 * @param props.onSelect - Callback with the formation value
 */
const FormationChipContainer = ({
  option,
  isSelected,
  onSelect,
}: FormationChipContainerProps) => {
  const handlePress = useCallback(
    () => onSelect(option.value),
    [onSelect, option.value]
  );

  return (
    <SelectableChip
      label={option.label}
      isSelected={isSelected}
      onPress={handlePress}
      shape="chip"
      testID={`formation-chip-${option.value}`}
      accessibilityLabel={`Select formation ${option.label}`}
    />
  );
};

FormationChipContainer.displayName = "FormationChipContainer";

export default memo(FormationChipContainer);
